import type { $Fetch } from 'nitropack'
import type { FetchOptions, FetchContext } from 'ofetch'

import { useAlertStore } from '@/store/alert'

import { ResponseCode, type ApiResponseType } from '@/types/service'
import { checkRequestIsValidIfNeeded } from '@/utils/request'
import { DEFAULT_LANG } from '@/constants/locales'
import { IS_DEV_MODE } from '@/constants/envs'

type SkipGeneralAlertList = { urlPattern: RegExp; otherCondition?: (context: FetchContext) => boolean }[]

const RETRY_COUNT = 3
const RETRY_DELAY_MS = 1000
const RETRY_STATUS_CODES = [408, 425, 500, 502, 503, 504]

export const createApiClient = (options?: FetchOptions, nuxtApp = useNuxtApp()) => {
  const config = useRuntimeConfig()
  const skipGeneralAlertList: SkipGeneralAlertList = [
    { urlPattern: /\/auth/ },
    { urlPattern: /\/promo/ },
    { urlPattern: /\/register/ },
    { urlPattern: /\/login/ },
    { urlPattern: /\/password\/recover/ },
    { urlPattern: /\/password\/reset/ },
    { urlPattern: /\/member$/, otherCondition: context => context.response?._data.code === ResponseCode.UNAUTHORIZED },
    { urlPattern: /\/member\/deletion\/\w+/ },
    { urlPattern: /\/member\/suspended/ },
    { urlPattern: /\/order$/ },
    { urlPattern: /\/order\/\d+$/, otherCondition: context => context.response?.status === 404 },
    { urlPattern: /\/order\/\d+\/edit/ },
    { urlPattern: /\/review\/\d+\/like/, otherCondition: context => context.response?._data?.code === ResponseCode.DUPLICATE_KEY },
    // ZOEKIT-18695: static content fetched directly from an external CDN (e.g. `branch.static_link`, a full absolute
    // URL that bypasses `baseURL` — see ofetch's `withBase`). This is non-critical, below-the-fold supplementary
    // content, so a failed request shouldn't show the app's global error toast. We don't yet know the CDN's fixed
    // host/path shape (BE contract still pending per ZOEKIT-18692), so match generically: any request whose
    // (already baseURL-resolved) URL is an absolute URL NOT pointing at our own API is treated as "external".
    // Currently this client is only ever called with relative paths except for this static-content case, so this
    // stays narrowly scoped in practice.
    {
      urlPattern: /.*/,
      otherCondition: context => {
        const url = typeof context.request === 'string' ? context.request : context.request?.url
        return typeof url === 'string' && url.startsWith('http') && !url.startsWith(config.public.apiBaseUrl)
      },
    },
  ]
  const baseURL = IS_DEV_MODE ? '/api/proxy' : config.public.apiBaseUrl

  // Capture context here, not inside callbacks — avoids "[nuxt] instance unavailable" during SSR $fetch retry.
  const { localeProperties } = nuxtApp.$i18n
  const alertStore = useAlertStore()

  return $fetch.create({
    baseURL,
    credentials: 'include',
    retry: RETRY_COUNT,
    retryStatusCodes: RETRY_STATUS_CODES,
    retryDelay: context => {
      const currentRetryCount = RETRY_COUNT - (context.options.retry || 0) + 1

      return currentRetryCount * RETRY_DELAY_MS
    },
    onRequest({ options, request }) {
      if (!checkRequestIsValidIfNeeded(request)) {
        throw new Error('Invalid request')
      }

      const acceptLang = localeProperties.value.name ?? DEFAULT_LANG

      options.headers.delete('common')
      options.headers = new Headers(options.headers)
      options.headers.set('Accept-Language', acceptLang)
      if (import.meta.client) {
        options.headers.set('Accept-Timezone', getTimeZone())
      } else {
        options.headers.set('Server-Side-Request', 'true')
      }
    },
    // onRequestError({ request, options, error }) {},
    // onResponse({ request, options, response }) {},
    onResponseError(context) {
      const isErrorSkipped = RETRY_STATUS_CODES.includes(context.response.status) && context.options.retry !== 0

      if (isErrorSkipped) {
        return
      }

      // FIXME: 過濾掉檢查登入時的錯誤訊息，但應該有更乾淨的做法
      const isAlertSkipped = skipGeneralAlertList.some(item => item.urlPattern.test(context.response.url) && (item.otherCondition?.(context) ?? true))

      if (context.response?._data?.message && !isAlertSkipped) {
        alertStore.error(context.response._data.message)
      }
    },
    ...options,
  })
}

const useApiClient = <T>(): $Fetch<ApiResponseType<T>> => {
  const nuxtApp = useNuxtApp()

  if (!nuxtApp._apiClient) {
    nuxtApp._apiClient = createApiClient(undefined, nuxtApp)
  }

  return nuxtApp._apiClient as $Fetch<ApiResponseType<T>>
}

export default useApiClient
